import { FrameContexts } from './messages.interface';
import {
  AppContext,
  AppInitInfo,
  IWebContentContext,
  IAppDefinition,
  UserInfo,
  BaseContext,
  AppInfo,
} from './hosted-web-content-sdk.interface';
import { IWindowProvider } from './event-listener.interface';
import { HubSdkTelemetryService } from './telemetry-service';
import { HubInfo } from '../meta-os-app-container.interface';
import { IAuthenticationUser, IBaseAuthenticationService, ITelemetryService } from './interfaces';
import { TelemetrySink } from '@microsoft/oteljs';

export abstract class WebContentSdkService {
  private initialized: boolean = false;
  private contextPromise?: Promise<AppContext>;
  protected origin?: string;
  protected appDefinition?: IAppDefinition;
  protected readonly telemetryService: HubSdkTelemetryService;
  private userClickTime!: number;

  constructor(
    // TODO: Resolve inability to access global window through IWindowProvider https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/746287
    protected hostWindow: Window | IWindowProvider,
    protected context: IWebContentContext,
    protected authenticationService: IBaseAuthenticationService,
    protected customTelemetryService?: ITelemetryService,
    protected onAppInitStateChanged?: (appInitState: AppInitInfo) => void,
    protected telemetrySink?: TelemetrySink
  ) {
    this.telemetryService = new HubSdkTelemetryService(
      this.getHubInfo(),
      context as BaseContext,
      this.customTelemetryService,
      this.telemetrySink
    );
  }

  /**
   * to check if sdk service is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * sets the SDK as initialized/un-initialized
   */
  public setInitialized(isInitialized: boolean): void {
    this.initialized = isInitialized;
  }

  /**
   * gets the Hub related info from appContext
   */
  public getHubInfo(): HubInfo {
    const hubInfo: HubInfo = {
      name: this.context.app.host.name,
      version: this.context.app.host.version,
      clientType: this.context.app.host.clientType,
      sessionId: this.context.app.host.sessionId,
    };
    return hubInfo;
  }

  /**
   * function to execute when app frame is unloaded
   */
  public unloadApp(appDefinition: IAppDefinition) {
    this.telemetryService.createScenario('appTermination', appDefinition.appId).stop();
  }

  /**
   * get HostWindow
   */
  public getHostWindow(): Window | IWindowProvider | null {
    return this.hostWindow;
  }

  /**
   * get FrameContext
   */
  public getFrameContext(): FrameContexts {
    return this.context.page.frameContext;
  }

  /**
   * Initialize authentication context
   * @param context: optional app specific context to override the base authentication context
   */
  public initializeAuthenticationContext = (context: IWebContentContext) => {
    this.context = context;
    this.contextPromise = this.authenticationService?.getUser().then((user: IAuthenticationUser) => {
      // fill user info and merge other details from Context
      const userInfo: UserInfo = {
        ...this.context?.user,
        id: user?.profile?.oid || '',
        displayName: user?.profile?.displayName || '',
        loginHint: user?.profile?.upn || '',
        userPrincipalName: user?.profile?.upn || '',
        tenant: {
          id: user?.profile?.tid || '',
          sku: this.context?.user?.tenant?.sku,
        },
      };

      // fill app info and merge other details from Context
      const appInfo: AppInfo = {
        ...this.context.app,
        userClickTime: this.getUserClickTime(),
      };

      // merge context and userInfo to form appContext
      const appContext: AppContext = {
        ...this.context,
        app: appInfo,
        user: userInfo,
      };
      this.telemetryService.setAppContext(appContext);

      return appContext;
    });
  };

  /**
   * Gets the MetaOS web context.
   * Context provided by the Hubs
   */
  public getContext = (): IWebContentContext => {
    return this.context;
  };

  /**
   * Gets the MetaOS app context.
   * Context provided to the Apps from AppSDK
   */
  public getAppContext = (): Promise<AppContext> => {
    if (this.contextPromise) {
      return this.contextPromise.then((context) => context).catch(() => this.context as AppContext);
    } else {
      return Promise.resolve(this.context as AppContext);
    }
  };

  public getAppDefinition = (): IAppDefinition | undefined => this.appDefinition;

  /**
   * updates current AppInitState value to the new one provided by caller
   *
   * @param appState new AppInitState to set
   */
  public setAppInitInfo = (appInitState: AppInitInfo): void => {
    if (this.onAppInitStateChanged) {
      this.onAppInitStateChanged(appInitState);
    }
  };

  /**
   * Gets time when user clicked on the app.
   */
  public getUserClickTime(): number {
    return this.userClickTime;
  }

  /**
   * Sets time when user clicked on the app.
   */
  protected setUserClickTime(value: number) {
    this.userClickTime = value;
  }

  /**
   * to send response message to app frame
   * @param id: id used for callback function
   * @param args: function arguments
   * @param isPartialResponse: is response partial
   */
  public abstract postResponseToApp(
    id: number | undefined,
    args?: (object | string | boolean)[],
    isPartialResponse?: boolean
  ): void;

  /**
   * Responsible for cleaning up of resources on SDK Unload
   */
  public abstract unloadSDK(): void;
}
